import { useCallback, useEffect, useRef } from 'react'
import type { SearchShortcutHandler } from './ShortcutContext'
import { shortcutLogger } from './shortcutLogger'

export const useSearchShortcut = () => {
  const onSearchInvoked = useRef<SearchShortcutHandler>(undefined)

  const clearOnSearchInvoked = useCallback((context: string) => {
    if (onSearchInvoked.current && onSearchInvoked.current.context !== context) {
      shortcutLogger.warn(
        `Cleared search context of '${onSearchInvoked.current.context}', while the caller wanted to clear '${context}', this is probably a user error.`,
      )
    }
    onSearchInvoked.current = undefined
  }, [])

  const setOnSearchInvoked = useCallback(
    (newHandler: SearchShortcutHandler) => {
      if (onSearchInvoked.current) {
        shortcutLogger.warn(
          `Already attached a search context with context name '${onSearchInvoked.current}', replacing with '${newHandler?.context}'`,
        )
      }
      onSearchInvoked.current = newHandler
      return () => clearOnSearchInvoked(newHandler.context)
    },
    [clearOnSearchInvoked],
  )

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        shortcutLogger.debug('Search shortcut abort')
        if (onSearchInvoked.current && document.activeElement) {
          ;(document.activeElement as HTMLElement).blur()
        }
        return
      }
      if (e.code === 'F3' || ((e.ctrlKey || e.metaKey) && e.code === 'KeyF')) {
        shortcutLogger.debug('Search Shortcut invoked')
        if (onSearchInvoked.current) {
          onSearchInvoked.current.onSearch(onSearchInvoked.current.context, e)
        }
      }
    }
    shortcutLogger.debug('Search Handler registered')
    document.addEventListener('keydown', handler)
    return () => {
      shortcutLogger.debug('Search Handler removed')
      document.removeEventListener('keydown', handler)
    }
  }, [])

  return {
    handler: onSearchInvoked,
    /**
     * Use to register a new handler when the search shortcut is being invoked.
     * The handler returns a callback that can be used to unregister the handler.
     * @example
     * const { search: { setOnSearchInvoked }} = useShortcutContext();
     * useEffect(() =>
     *  // automatically returns a callback that unregisters the shortcut when its lifetime has passed
     *  setOnSearchInvoked({
     *    context: "handler-context-name",
     *    onSearch: () => { ... }
     *  }), []
     * )
     */
    setOnSearchInvoked,
    clearOnSearchInvoked,
  }
}
